/**
 * save/deserialize.ts — rebuild a live GameState from a SaveBlob.
 *
 * Inverse of serialize.ts. The static catalogues (shipTemplates,
 * weapons, techs) come from the freshly-loaded LoadedGameData rather
 * than from the blob — see serialize.ts header for the rationale.
 *
 * Validation strategy: cheap structural checks (formatVersion,
 * required top-level fields). We do *not* deep-validate every nested
 * record — saves come from our own serialiser, so a well-formed
 * blob with the matching formatVersion is trustworthy. If we ever
 * accept saves from untrusted sources (export / import), tighten
 * this.
 */

import type { LoadedGameData } from '../data/loader';
import type {
  Empire,
  EnvironmentEntity,
  GameState,
  Missile,
  Planet,
  ShipInstance,
  StarSystem,
  Structure,
  VisibilityRecord,
  WarpGateLink,
} from '../state/types';
import {
  SAVE_BLOB_VERSION,
  type SaveBlob,
  type SerializedEmpire,
  type SerializedTeamVision,
} from './serialize';

/** Thrown when a SaveBlob can't be loaded (version mismatch, malformed). */
export class SaveLoadError extends Error {
  constructor(
    message: string,
    /** Hint for the UI — concise enough to surface in a toast. */
    readonly userMessage: string,
  ) {
    super(message);
    this.name = 'SaveLoadError';
  }
}

function deserializeEmpire(s: SerializedEmpire): Empire {
  return {
    id: s.id,
    name: s.name,
    raceId: s.raceId,
    isPlayer: s.isPlayer,
    resources: { ...s.resources },
    planetIds: [...s.planetIds],
    researchedTechs: new Set(s.researchedTechs),
    activeResearch: s.activeResearch ? { ...s.activeResearch } : null,
    shipIds: [...s.shipIds],
    color: s.color,
    aiDifficulty: s.aiDifficulty,
    aiBehaviour: s.aiBehaviour,
    // v4M3.1 — pre-v14 saves lack `teamId`; back-fill to `null` (the
    // "solo / legacy" default). v14+ saves carry the value verbatim.
    teamId: s.teamId ?? null,
    scoutedSystemIds: new Set(s.scoutedSystemIds),
    diplomaticFocus: s.diplomaticFocus,
  };
}

function deserializeTeamVision(
  serialized: SerializedTeamVision,
): GameState['teamVision'] {
  const out: GameState['teamVision'] = new Map();
  for (const [teamId, innerArr] of serialized) {
    const inner = new Map<number, VisibilityRecord>();
    for (const [shipId, rec] of innerArr) inner.set(shipId, { ...rec });
    out.set(teamId, inner);
  }
  return out;
}

/** Top-level structural sanity check. Throws SaveLoadError on mismatch. */
function validateBlobShape(blob: unknown): asserts blob is SaveBlob {
  if (typeof blob !== 'object' || blob === null) {
    throw new SaveLoadError('Blob is not an object', 'Save file is corrupted.');
  }
  const obj = blob as Record<string, unknown>;
  if (typeof obj.header !== 'object' || obj.header === null) {
    throw new SaveLoadError(
      'Missing header',
      'Save file is corrupted — header missing.',
    );
  }
  if (typeof obj.body !== 'object' || obj.body === null) {
    throw new SaveLoadError(
      'Missing body',
      'Save file is corrupted — body missing.',
    );
  }
  const header = obj.header as Record<string, unknown>;
  if (typeof header.formatVersion !== 'number') {
    throw new SaveLoadError(
      'header.formatVersion missing or wrong type',
      'Save file is corrupted — version missing.',
    );
  }
  if (header.formatVersion !== SAVE_BLOB_VERSION) {
    throw new SaveLoadError(
      `formatVersion ${header.formatVersion} != current ${SAVE_BLOB_VERSION}`,
      `Save was created with format version ${header.formatVersion}; this build expects ${SAVE_BLOB_VERSION}. Saves can't be migrated automatically.`,
    );
  }
}

/**
 * Restore a GameState from a SaveBlob. The static catalogues
 * (shipTemplates, weapons, techs) are pulled from `data` — pass the
 * same LoadedGameData your fresh-game path would use.
 *
 * Throws {@link SaveLoadError} if the blob is malformed or its
 * formatVersion doesn't match this build.
 */
export function deserializeGameState(
  blob: unknown,
  data: LoadedGameData,
): GameState {
  validateBlobShape(blob);
  const body = blob.body;

  const empires = new Map<number, Empire>();
  for (const [id, s] of body.empires) empires.set(id, deserializeEmpire(s));

  const planets = new Map<number, Planet>();
  for (const [id, p] of body.planets) {
    // Defensive shallow copy so post-load mutations to the live state
    // don't accidentally write back into the blob. `terraforming` is
    // nullable; clone only when present.
    planets.set(id, {
      ...p,
      structureIds: [...p.structureIds],
      shipBuildQueue: p.shipBuildQueue.map((q) => ({ ...q })),
      terraforming: p.terraforming ? { ...p.terraforming } : null,
      // v3M21.1 — rebuild diplomacy fields. influence is a Map at
      // runtime, serialised as Array<[empireId, points]>; the others
      // are JSON-clean and ride through via spread above (this assignment
      // overrides the spread for `influence` only).
      influence: new Map(p.influence),
      tradingWith: [...p.tradingWith],
    });
  }

  const systems = new Map<number, StarSystem>();
  for (const [id, s] of body.systems) {
    systems.set(id, {
      ...s,
      planetIds: [...s.planetIds],
      adjacentSystemIds: [...s.adjacentSystemIds],
    });
  }

  // v3M18.1 — restore Warp Gate connections. Tolerate older blobs that
  // pre-date the field by defaulting to an empty Map (the format version
  // bump rejects pre-v7 saves up front, so this branch is defensive only).
  const warpGateLinks = new Map<number, WarpGateLink>();
  if (body.warpGateLinks) {
    for (const [id, l] of body.warpGateLinks) warpGateLinks.set(id, { ...l });
  }

  const ships = new Map<number, ShipInstance>();
  for (const [id, s] of body.ships) {
    ships.set(id, {
      ...s,
      moveOrder: s.moveOrder ? { ...s.moveOrder } : null,
      constructionOrder: s.constructionOrder ? { ...s.constructionOrder } : null,
      bombardOrder: s.bombardOrder ? { ...s.bombardOrder } : null,
      // Restore Infinity from the -1 sentinel set by serialize.ts. Any
      // legitimate non-negative ammo passes through unchanged.
      weaponSlots: s.weaponSlots.map((w) => ({
        cooldownTicks: w.cooldownTicks,
        ammoRemaining: w.ammoRemaining === -1 ? Infinity : w.ammoRemaining,
      })),
    });
  }

  // v3M28.1 — persistent missile entities. Plain shallow clone per
  // entry; Missile carries only number / string / boolean fields so
  // no nested Map/Set restoration is needed. The hard-reject version
  // gate at the top of this function guarantees body.missiles is
  // present (pre-v13 saves never reach this point).
  const missiles = new Map<number, Missile>();
  for (const [id, m] of body.missiles) missiles.set(id, { ...m });

  const structures = new Map<number, Structure>();
  for (const [id, s] of body.structures) structures.set(id, { ...s });

  const environment = new Map<number, EnvironmentEntity>();
  for (const [id, e] of body.environment) environment.set(id, { ...e });

  return {
    version: body.version,
    tick: body.tick,
    rng: { s: body.rng.s >>> 0 },
    nextEntityId: body.nextEntityId,
    empires,
    planets,
    systems,
    warpGateLinks,
    ships,
    missiles,
    structures,
    environment,
    shipTemplates: data.shipTemplates,
    auras: data.auras,
    weapons: data.weaponDefs,
    techs: data.techDefs,
    habitability: data.habitability,
    aiBehaviours: data.aiBehaviours,
    races: data.races,
    structuresData: data.structures,
    balanceData: data.balance,
    teamVision: deserializeTeamVision(body.teamVision),
    // v3M19 — `combatEvents` is transient render-layer state, deliberately
    // excluded from the save body. Always re-attach as an empty array on
    // load; the next `advanceCombat` pass will populate it as combat
    // resolves.
    combatEvents: [],
    // v4M1.3 — `commandRejections` is transient UI-layer state populated by
    // `applyCommands` and consumed by the alert-feed on the same tick.
    // Excluded from the save body; re-attach as an empty array on load.
    commandRejections: [],
    playerEmpireId: body.playerEmpireId,
    // v3M12 — older blobs (pre-v3M12) won't have `gameOutcome`; default
    // them to `'in-progress'` so legacy saves keep behaving like an
    // active game when loaded.
    gameOutcome: body.gameOutcome ?? 'in-progress',
  };
}
